import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, Users, Target, Share2, Twitter, Linkedin, MessageCircle, Link as LinkIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const formatMoney = (amount, currency, lang) => {
  try {
    return new Intl.NumberFormat(lang === "en" ? "en-US" : "fr-FR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (_) {
    return `${amount} ${currency}`;
  }
};

const CampaignDetailPage = () => {
  const { t, lang } = useLanguage();
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/campaigns/${slug}`)
      .then(({ data }) => setC(data))
      .catch(() => setC(null))
      .finally(() => setLoading(false));
    api
      .get("/campaigns")
      .then(({ data }) => setOthers(Array.isArray(data) ? data.filter((x) => x.slug !== slug).slice(0, 3) : []))
      .catch(() => setOthers([]));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center" data-testid="campaign-detail-loading">
        <span className="text-[#64748B]">{t.campaignsPage.loading}</span>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="campaign-detail-notfound">
        <Navbar />
        <div className="container-narrow py-32 text-center">
          <p className="text-[#64748B]">{t.campaignDetail.notFound}</p>
          <Link to={lang === "en" ? "/campaigns" : "/campagnes"} className="inline-flex items-center gap-2 mt-6 text-[#002B49] hover:text-[#C5A059]">
            <ArrowLeft size={14} /> {t.campaignDetail.back}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const title = lang === "en" ? c.title_en : c.title_fr;
  const description = lang === "en" ? c.description_en : c.description_fr;
  const detailPath = lang === "en" ? `/campaigns/${c.slug}` : `/campagnes/${c.slug}`;
  const remaining = Math.max(0, c.goal_amount - c.raised_amount);
  const completed = (c.percentage || 0) >= 100;
  const shareUrl = `${window.location.origin}${detailPath}`;
  const shareText = lang === "en"
    ? `Support "${title}" — Fondation Cœur Solidaire`
    : `Soutenez « ${title} » — Fondation Cœur Solidaire`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t.campaignDetail.copied);
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="campaign-detail-page">
      <Seo
        title={title}
        description={description.slice(0, 200)}
        path={detailPath}
        image={c.image_url}
        lang={lang}
        type="article"
      />
      <Navbar />

      <section className="pt-28 md:pt-32 pb-12 bg-gradient-to-b from-[#002B49] to-[#001F35] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 50%, #C5A059 0%, transparent 50%)" }} />
        <div className="container-narrow relative">
          <Link
            to={lang === "en" ? "/campaigns" : "/campagnes"}
            className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors"
            data-testid="campaign-back"
          >
            <ArrowLeft size={14} /> {t.campaignDetail.back}
          </Link>
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-medium leading-[1.1] max-w-4xl" data-testid="campaign-title">
            {title}
          </h1>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-narrow grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {c.image_url && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="aspect-[16/9] rounded-2xl overflow-hidden bg-[#F3F0EA] mb-8"
              >
                <img src={c.image_url} alt={title} className="w-full h-full object-cover" />
              </motion.div>
            )}
            <div className="prose prose-lg max-w-none text-[#334155]" data-testid="campaign-description">
              {description.split("\n").filter(Boolean).map((para, i) => (
                <p key={i} className="leading-relaxed mb-5">{para}</p>
              ))}
            </div>

            {/* Share */}
            <div className="mt-10 pt-8 border-t border-[#E5E7EB]">
              <h3 className="text-sm font-semibold text-[#002B49] uppercase tracking-wider mb-3 flex items-center gap-2">
                <Share2 size={14} /> {t.campaignDetail.shareTitle}
              </h3>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#002B49] hover:border-[#C5A059] transition-colors"
                  data-testid="share-twitter"
                >
                  <Twitter size={14} /> {t.campaignDetail.shareTwitter}
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#002B49] hover:border-[#C5A059] transition-colors"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle size={14} /> {t.campaignDetail.shareWhatsapp}
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#002B49] hover:border-[#C5A059] transition-colors"
                  data-testid="share-linkedin"
                >
                  <Linkedin size={14} /> {t.campaignDetail.shareLinkedin}
                </a>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#002B49] hover:border-[#C5A059] transition-colors"
                  data-testid="share-copy"
                >
                  <LinkIcon size={14} /> {t.campaignDetail.shareCopy}
                </button>
              </div>
            </div>
          </div>

          {/* Sticky progress */}
          <aside className="lg:sticky lg:top-28 lg:self-start space-y-5">
            <div className="bg-white border border-black/5 rounded-2xl p-6 shadow-[0_8px_40px_-20px_rgba(0,43,73,0.2)]" data-testid="campaign-progress-panel">
              <div className="font-serif text-3xl md:text-4xl text-[#002B49] font-semibold">
                {formatMoney(c.raised_amount, c.currency, lang)}
              </div>
              <p className="text-xs text-[#64748B] uppercase tracking-wider mt-1">
                {t.campaignDetail.raised} · {t.campaignDetail.goal} {formatMoney(c.goal_amount, c.currency, lang)}
              </p>
              <div className="h-2.5 bg-[#F3F0EA] rounded-full overflow-hidden mt-5">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#002B49] to-[#C5A059] rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(c.percentage, 100)}%` }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-[#64748B] mt-3">
                <span>{c.percentage}%</span>
                <span className="inline-flex items-center gap-1"><Users size={12} /> {c.donor_count} {t.campaignDetail.donors}</span>
              </div>
              {!completed && (
                <p className="text-xs text-[#64748B] mt-2">
                  {formatMoney(remaining, c.currency, lang)} {t.campaignsPage.remaining}
                </p>
              )}
              {c.deadline && (
                <p className="text-xs text-[#64748B] mt-1 inline-flex items-center gap-1">
                  <Calendar size={12} /> {t.campaignDetail.deadline}: {new Date(c.deadline).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                </p>
              )}
              <Link
                to={`/?campaign=${c.slug}#donate`}
                className="block w-full text-center mt-6 bg-[#C5A059] hover:bg-[#B08F4D] text-white px-5 py-3.5 rounded-lg font-semibold transition-colors"
                data-testid="campaign-support-cta"
              >
                {t.campaignDetail.supportNow}
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {others.length > 0 && (
        <section className="py-16 bg-white border-t border-[#E5E7EB]">
          <div className="container-narrow">
            <h2 className="font-serif text-2xl md:text-3xl text-[#002B49] font-medium mb-8">{t.campaignDetail.relatedTitle}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {others.map((o, i) => (
                <Link
                  key={o.id}
                  to={lang === "en" ? `/campaigns/${o.slug}` : `/campagnes/${o.slug}`}
                  className="group block bg-[#FDFBF7] rounded-2xl overflow-hidden border border-black/5 hover:border-[#C5A059] transition-colors"
                  data-testid={`related-campaign-${i}`}
                >
                  {o.image_url && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={o.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-serif text-lg font-semibold text-[#002B49] group-hover:text-[#C5A059] transition-colors">
                      {lang === "en" ? o.title_en : o.title_fr}
                    </h3>
                    <p className="text-xs text-[#64748B] mt-1.5">
                      {formatMoney(o.raised_amount, o.currency, lang)} {t.campaignDetail.raised} · {o.percentage}%
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default CampaignDetailPage;
